import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { layouts } from 'seal-ui-components';
import mixpanelEvents from '@Mixpanel';
import { Card } from '@Components/UI/Card';
import Selectable from '@Components/Selectable';
import { LongTrimmedTitle } from '@Components/LongTitle';
import { decodeHtmlText } from '@Utils/commonUtils';
import { countClauses } from '@Utils/clausesUtils';
import {
    ClauseDetailsButton,
    Tags,
    ClauseSummaryCardWrapper,
    ClauseTextWrapper,
    Dot
} from './ClauseSummaryCard.style';
import { CLAUSE_LIBRARY } from '@Constants/types';
import store from '@Store';

const { VerticalBoxDivider } = layouts;

const ClauseSummaryCard = ({
    clauseId,
    onToggle,
    isExpanded,
    hideControls,
    clause,
    hasChildren,
    parentId
}) => {
    const history = useHistory();
    const location = useLocation();

    const { clauseCategoryTaxonomy, name, clauses = [] } = clause;

    const modalHeader = clauseCategoryTaxonomy || name;

    const { text: originalText, description: originalDesc } = clauses[0] || {};
    const [subTitle, ...rest] = modalHeader.split('>').reverse();
    const title = rest.reverse().join('/');

    const totalClausesCount = countClauses(0, clause);

    const description = decodeHtmlText(originalDesc);
    const text = decodeHtmlText(originalText);
    const tagRegex = /(?:^|\W)#(\w+)(?!\w)/g;
    const showDivider = text?.length > 0 && description?.length > 0;

    const getTags = clauseDescription =>
        clauseDescription ? clauseDescription.match(tagRegex) : [];

    const goToClauseDetails = () => {
        if (clauses.length && clauseId) {
            store.dispatch({ type: CLAUSE_LIBRARY.SET_DRAWER_OPEN_STATE, payload: true });
            history.push({
                pathname: `/clauses/${clauseId}`,
                state: {
                    clauseDetailsOverlay: location
                }
            });
            mixpanelEvents.clauseLibraryShowClauseDetails();
        }
    };

    const CardHeader = () => (
        <layouts.HorizontalBox>
            <LongTrimmedTitle
                data-testid="card-header"
                title={`${subTitle || name}  (${totalClausesCount})`}
            />
            {hasChildren && <Dot />}
        </layouts.HorizontalBox>
    );

    return (
        <ClauseSummaryCardWrapper>
            <Card
                color="rgb(238, 238, 238)"
                toggle={() => onToggle(clauseId, isExpanded)}
                isOpened={isExpanded}
                renderHeader={CardHeader}
                stackEffect={clauses && clauses.length > 1}
                hideControls={hideControls}
                collapsible
            >
                {parentId && <LongTrimmedTitle data-testid="sub-title" title={title || subTitle} />}
                {clauses.length > 0 && (
                    <ClauseDetailsButton
                        className="go-to-clause-details"
                        onClick={goToClauseDetails}
                        data-testid="go-to-clause-details"
                    >
                        Show more details
                    </ClauseDetailsButton>
                )}
                <Tags data-testid="tags">{getTags(description)}</Tags>
                <ClauseTextWrapper>
                    <Selectable
                        data-testid="selectable-description"
                        className="selectable"
                        originalText={decodeHtmlText(description)}
                        clampNoButtons
                    />
                </ClauseTextWrapper>
                {showDivider && <VerticalBoxDivider />}
                <ClauseTextWrapper>
                    <Selectable
                        data-testid="selectable-text"
                        className="selectable"
                        originalText={decodeHtmlText(text)}
                        withInsert
                        clampNoButtons
                        clausesCount={clauses.length}
                        clauseId={clauses.length > 0 && clauses[0].id}
                        clausePriority={clauses.length > 0 && clauses[0].clausePriority}
                    />
                </ClauseTextWrapper>
            </Card>
            {clauses &&
                clauses.length > 1 &&
                isExpanded && (
                    <ClauseDetailsButton className="show-fallback" onClick={goToClauseDetails}>
                        Show fallback language ({clauses.length - 1} more)
                    </ClauseDetailsButton>
                )}
        </ClauseSummaryCardWrapper>
    );
};

export default ClauseSummaryCard;
